import { get, has } from 'lodash';
import { CellType } from '../models';
import { CellTypeOption, ContentAlignment, TableColumn, ColumnDef } from '..';
import { BooleanCell } from './boolean';
import { DurationCell } from './duration';
import { NumberCell } from './number';
import { StringCell } from './string';
import { TimestampCell } from './timestamp';
import { HealthCell } from './health';
import { TimeseriesCell } from './timeseries';
import { EcpLinkCell } from './ecpLink';
import { DEFAULT_EMPTY_CELL_PLACEHOLDER } from '../constants';

export interface getDefaultColumnDefParamsType<TData> {
  col: TableColumn<TData>;
  isClientSideData?: boolean;
  metadata?: Record<string, unknown>;
}
export interface CellTypeStrategy<TData> {
  getDefaultColumnDef(params: getDefaultColumnDefParamsType<TData>): Partial<ColumnDef<TData>>;
}

class CellTypeContext<TData> {
  private cellTypeStrategy: CellTypeStrategy<TData>;

  constructor(cellType: CellTypeStrategy<TData>) {
    this.cellTypeStrategy = cellType;
  }

  public getDefaultColumnDef(params: getDefaultColumnDefParamsType<TData>): Partial<ColumnDef<TData>> {
    return this.cellTypeStrategy.getDefaultColumnDef(params);
  }
}

export class CellStrategy {
  private static CELL_TYPE_STRATEGY_MAP = {
    [CellType.BOOLEAN as CellTypeOption]: BooleanCell,
    [CellType.DURATION as CellTypeOption]: DurationCell,
    [CellType.NUMBER as CellTypeOption]: NumberCell,
    [CellType.STRING as CellTypeOption]: StringCell,
    [CellType.TIMESTAMP as CellTypeOption]: TimestampCell,
    [CellType.HEALTH as CellTypeOption]: HealthCell,
    [CellType.TIMESERIES as CellTypeOption]: TimeseriesCell,
    [CellType.ECP_LINK as CellTypeOption]: EcpLinkCell,
  };

  /**
   * This method returns the default uik Grid ColumnDef object given info for the column
   * @param col column information
   * @returns default ColumnDef object to use for column
   */
  static getDefaultColumnDef<TData>(params: getDefaultColumnDefParamsType<TData>): Partial<ColumnDef<TData>> {
    const cellType = params.col.cellType as CellTypeOption;

    if (has(CellStrategy.CELL_TYPE_STRATEGY_MAP, cellType)) {
      const cellTypeStrategy = new CellStrategy.CELL_TYPE_STRATEGY_MAP[cellType]() as CellTypeStrategy<TData>;
      const cellTypeContext = new CellTypeContext<TData>(cellTypeStrategy);
      return cellTypeContext.getDefaultColumnDef(params);
    }

    // if cell type is not supported
    return {
      valueFormatter: param => {
        const value = get(param, 'value');
        return value != null ? String(value) : DEFAULT_EMPTY_CELL_PLACEHOLDER;
      },
      // meta: {
      //   align: ContentAlignment.LEFT,
      // },
    };
  }
}
